import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { SidePanelService, SplitViewService } from '@ebw-monorepo/utils';
import Feature, { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point';
import { Draw, Select } from 'ol/interaction';
import { DrawEvent } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import { distinctUntilChanged, filter, finalize, Subscription, take } from 'rxjs';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { MapService } from '../../../../services/map.service';
import { LayerStylesService } from '../../../../services/layer-styles.service';
import { RischioAnaliticoService } from '../../../../services/rischio-analitico.service';
import { RischioAnaliticoStateService } from '../../../../services/rischio-analitico-state.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { ControlliService } from '../../../../services/controlli.service';
import { LimiteLegge } from '../../../../models/controlli.model';
import {
  COLORE_CLASSE_ASSENTE,
  COLORI_CRITICALITY,
  COLORI_RISK_CLASS,
  filtriDefault,
  hexToRgb,
  idStileClasse,
  isNessunDatoClassificabile,
  messaggioErrore,
  OpzioneComune,
  PuntoRischioResponse,
  richiestaPunti,
  RischioAnaliticoFiltriValue,
  TipoPuntoCampionamentoLookup
} from '../../../../models/rischio-analitico.model';
import { click } from 'ol/events/condition';
import { ToOpenHistoryModalComponent } from '../../checkin-analysis/to-open-history/to-open-history-modal.component';
import { RischioAnaliticoDetailComponent } from './rischio-analitico-detail/rischio-analitico-detail.component';

// avvisi lunghi (es. analiti non classificabili): il default di 3s non basta a leggerli
const SNACKBAR_AVVISO: MatSnackBarConfig = { duration: 6000, verticalPosition: 'top' };

/**
 * Container della sezione "Monitoraggio rischio analitico", agganciata al tab
 * "Visualizzazione" di ControlliComponent. Due sotto-tab: "Mappa" (mappa colorata + filtri,
 * con 3 schermi interni: mappa/dettaglio/drilldown) e "Riepilogo" (heatmap+tabella, sempre 95°perc).
 */
@Component({
  selector: 'ebw-monorepo-rischio-analitico',
  templateUrl: './rischio-analitico.component.html',
  styleUrls: ['./rischio-analitico.component.scss']
})
export class RischioAnaliticoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isActive = true;

  selectedTabIndex = 0;

  opzioniComune: OpzioneComune[] = [];
  opzioniTipologia: TipoPuntoCampionamentoLookup[] = [];
  opzioniAnaliti: LimiteLegge[] = [];

  poligonoAttivo = false;

  private readonly DRAW_INTERACTION_KEY = 'RISCHIO_ANALITICO_DRAW';

  private _layerPunti: VectorLayer<any>;
  private _sourcePunti: VectorSource;
  private readonly _styleCache = new Map<string, Style>();
  private _selectInteraction: Select;

  private _layerPoligono: VectorLayer<any>;
  private _sourcePoligono: VectorSource;
  private _drawInteraction: Draw;

  private _splitViewLayoutSub: Subscription;
  private _mappaProntaSub: Subscription;
  private _riepilogoApertoSub: Subscription;

  constructor(
    private _sidePanelService: SidePanelService,
    private _mapService: MapService,
    private _layerStylesService: LayerStylesService,
    private _rischioAnaliticoService: RischioAnaliticoService,
    private _controlliService: ControlliService,
    public state: RischioAnaliticoStateService,
    private _splitViewService: SplitViewService,
    private _snackbarService: SnackbarService
  ) { }

  ngOnInit(): void {
    this.state.analyticRiskIsOpen$.next(true);
    if (this.isActive) this._applyPanelWidth();
    this._caricaOpzioniFiltri();
    this._initLayerPuntiQuandoMappaPronta();

    // il pannello Riepilogo segnala l'apertura dal proprio costruttore: heatmap e riepilogo si svuotano
    // subito, cosi' non mostra nemmeno per un istante i dati di un'apertura precedente
    this._riepilogoApertoSub = this.state.riepilogoAperto$
      .pipe(distinctUntilChanged(), filter((aperto) => aperto))
      .subscribe(() => this._caricaRiepilogo());

    // il pannello di dettaglio puo' chiudersi in piu' modi (bottone "Chiudi", click su area
    // vuota della mappa, apertura di un altro pannello): qui intercettiamo tutti i casi cosi'
    // il punto non resta segnato come selezionato quando il dettaglio non e' piu' visibile
    this._splitViewLayoutSub = this._splitViewService.viewLayountConfig$.subscribe((conf) => {
      if (conf && !conf.isEnable) {
        this._deselezionaPunto();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.isActive || changes.isActive.firstChange) return;
    if (this.isActive) {
      this._applyPanelWidth();
    } else {
      this._sidePanelService.wideOverride$.next(null);
    }
  }

  ngOnDestroy(): void {
    this._splitViewLayoutSub?.unsubscribe();
    this._mappaProntaSub?.unsubscribe();
    this._riepilogoApertoSub?.unsubscribe();
    this._sidePanelService.halfOverride$.next(null);
    this._sidePanelService.wideOverride$.next(null);
    if (this._layerPunti) this._mapService.openLayersMap.removeLayer(this._layerPunti);
    if (this._layerPoligono) this._mapService.openLayersMap.removeLayer(this._layerPoligono);
    if (this._drawInteraction) this._mapService.removeMapInteraction(this.DRAW_INTERACTION_KEY);
    if (this._selectInteraction) this._mapService.openLayersMap.removeInteraction(this._selectInteraction);
    this.state.analyticRiskIsOpen$.next(false);
    this.state.detailFeature$.next(undefined);
  }

  private _deselezionaPunto(): void {
    this._selectInteraction?.getFeatures().clear();
    this.state.detailFeature$.next([]);
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;

    if (index === 1) {
      this._sidePanelService.halfOverride$.next(true);
      setTimeout(() => this._mapService.resizeOpenLayersMap(), 300);
    } else {
      this._sidePanelService.halfOverride$.next(false);
      setTimeout(() => this._mapService.resizeOpenLayersMap(), 300);
    }
  }

  // narrow per entrambi i sotto-tab: la mappa resta sempre visibile nell'area principale
  private _applyPanelWidth(): void {
    this._sidePanelService.wideOverride$.next(false);
    setTimeout(() => this._mapService.resizeOpenLayersMap(), 300);
  }

  onFiltriApplica(valore: RischioAnaliticoFiltriValue): void {
    this.state.filtri = valore;
    this._caricaPunti();
  }

  onFiltriReset(): void {
    this.state.filtri = filtriDefault();
    this._resetPoligono();
    this._caricaPunti();
  }

  onDisegnaPoligono(): void {
    if (!this._layerPoligono) {
      this._sourcePoligono = new VectorSource({ wrapX: false });
      this._layerPoligono = new VectorLayer({
        source: this._sourcePoligono,
        style: new Style({ stroke: new Stroke({ color: '#1976d2', width: 2 }), fill: new Fill({ color: 'rgba(25, 118, 210, 0.15)' }) })
      });
      this._layerPoligono.setZIndex(26);
      this._mapService.openLayersMap.addLayer(this._layerPoligono);
    }
    this._sourcePoligono.clear();

    this._drawInteraction = new Draw({ source: this._sourcePoligono, type: 'Polygon' });
    this._mapService.addMapInteraction(this.DRAW_INTERACTION_KEY, this._drawInteraction);
    this._mapService.customMapClickEnable$.next(true);

    this._drawInteraction.once('drawend', (event: DrawEvent) => {
      const geometria = event.feature.getGeometry().clone().transform('EPSG:3857', 'EPSG:4326');
      this.state.poligono = new GeoJSON().writeGeometryObject(geometria);
      this.poligonoAttivo = true;
      this._mapService.removeMapInteraction(this.DRAW_INTERACTION_KEY);
      this._mapService.customMapClickEnable$.next(false);
      this._caricaPunti();
    });
  }

  onRimuoviPoligono(): void {
    this._resetPoligono();
    this._caricaPunti();
  }

  // svuota l'area disegnata e interrompe un eventuale disegno in corso, senza ricaricare i punti
  private _resetPoligono(): void {
    if (this._drawInteraction) {
      this._mapService.removeMapInteraction(this.DRAW_INTERACTION_KEY);
      this._mapService.customMapClickEnable$.next(false);
    }
    if (this._sourcePoligono) this._sourcePoligono.clear();
    this.state.poligono = null;
    this.poligonoAttivo = false;
  }

  // il layer va creato una sola volta (non ad ogni volta che isActive torna true), qui in modo
  // che sia gia' pronto anche se l'utente apre "Visualizzazione" prima di "Caricamenti"
  private _initLayerPuntiSeNecessario(): void {
    if (this._layerPunti) return;

    this._registraStiliClasse();

    this._sourcePunti = new VectorSource({ wrapX: false });
    this._layerPunti = new VectorLayer({
      source: this._sourcePunti,
      style: (feature: FeatureLike) => this._stileFeature(feature)
    });
    this._layerPunti.setZIndex(25);
    this._mapService.openLayersMap.addLayer(this._layerPunti);

    this._selectInteraction = new Select({
      condition: () => false,
      layers: [this._layerPunti]
    });

    this._mapService.openLayersMap.addInteraction(this._selectInteraction);

    this._mapService.openLayersMap.on('click', (evt) => {
      const selectedFeatures = this._selectInteraction.getFeatures();
      selectedFeatures.clear(); // clear previous selection

      this._mapService.openLayersMap.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          if (layer === this._layerPunti && feature instanceof Feature) {
            selectedFeatures.push(feature);
          }
        },
        {
          hitTolerance: 3 // optional, widen the hit area a bit
        }
      );

      if (selectedFeatures.getLength() !== 0) {
        this._splitViewService.show(RischioAnaliticoDetailComponent, 'rischio-analitico-detail', null, {
          title: 'Elenco elementi',
          icon: 'featured_play_list',
          hideExpandActions: true
        });
        this._splitViewService.layoutConfChange({ isEnable: true, halfScreen: false, collapsed: false });

        this.state.detailFeature$.next(selectedFeatures.getArray());
      } else {
        this.state.detailFeature$.next([]);
        this._splitViewService.destroy();
      }
    });

    this._mapService.openLayersMap.on('pointermove', (evt) => {
      const sopraPunto = this._mapService.openLayersMap.hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === this._layerPunti,
        hitTolerance: 3
      });
      this._mapService.openLayersMap.getTargetElement().style.cursor = sopraPunto ? 'pointer' : '';
    });

    this._caricaPunti();
  }

  // dopo un refresh il pannello puo' riaprirsi prima che la mappa OpenLayers esista: il layer dei
  // punti si aggancia appena la mappa e' pronta, invece di fallire e non caricare piu' nulla
  private _initLayerPuntiQuandoMappaPronta(): void {
    const mappaPronta = () => this._mapService.readyOpenLayersMap && !!this._mapService.openLayersMap;
    if (mappaPronta()) {
      this._initLayerPuntiSeNecessario();
      return;
    }
    this._mappaProntaSub = this._mapService.readyOpenLayersMapChanged.pipe(filter(mappaPronta), take(1)).subscribe(() => this._initLayerPuntiSeNecessario());
  }

  // le opzioni dei filtri non dipendono dalla mappa
  private _caricaOpzioniFiltri(): void {
    this._rischioAnaliticoService.getTipologiePuntoCampionamento().subscribe({
      next: (tipologie) => (this.opzioniTipologia = [...tipologie].sort((a, b) => a.codice.localeCompare(b.codice))),
      error: (err) => this._notificaErrore(err, "Impossibile caricare l'elenco delle tipologie di punto")
    });
    this._controlliService.getParametriAnalitici().subscribe({
      next: (risposta) => (this.opzioniAnaliti = [...risposta.parametri].sort((a, b) => a.codice.localeCompare(b.codice))),
      error: (err) => this._notificaErrore(err, "Impossibile caricare l'elenco degli analiti")
    });
    // elenco fisso, indipendente dai filtri applicati: se derivato dai punti gia' filtrati (come
    // prima), selezionare un comune faceva sparire tutti gli altri dalle opzioni disponibili
    this._rischioAnaliticoService.getComuni().subscribe({
      next: (comuni) => (this.opzioniComune = comuni),
      error: (err) => this._notificaErrore(err, "Impossibile caricare l'elenco dei comuni")
    });
  }

  private _notificaErrore(err: unknown, testoGenerico: string): void {
    console.error(testoGenerico, err);
    this._snackbarService.open(messaggioErrore(err, testoGenerico), SNACKBAR_AVVISO);
  }


  private _chiudiDettaglioSeAperto(): void {
    if (this._splitViewService.contextId === 'rischio-analitico-detail') {
      this.state.detailFeature$.next([]);
      this._splitViewService.destroy();
    }
  }

  private _caricaPunti(): void {
    this._chiudiDettaglioSeAperto();
    const richiesta = richiestaPunti(this.state.filtri, this.state.poligono);
    this._rischioAnaliticoService.getPunti(richiesta).subscribe({
      next: ({ punti, avviso }) => {
        this._popolaLayer(punti);
        if (avviso) this._snackbarService.open(avviso, SNACKBAR_AVVISO);
      },
      error: (err) => {
        this._sourcePunti.clear();
        this._notificaErrore(err, 'Errore nel caricamento dei punti di campionamento');
      }
    });

    if (this.state.riepilogoAperto$.value) this._caricaRiepilogo();
  }

  // heatmap e riepilogo (chiamate a Kode) solo con il Riepilogo aperto: all'apertura del pannello
  // e a ogni nuova applicazione dei filtri finche' resta aperto
  private _caricaRiepilogo(): void {
    const richiesta = richiestaPunti(this.state.filtri, this.state.poligono);
    this.state.heatmap$.next([]);
    this.state.riepilogo$.next([]);
    this.state.caricamentoRiepilogo$.next(true);
    let chiamateInCorso = 2;
    const fineChiamata = () => {
      chiamateInCorso--;
      if (chiamateInCorso === 0) this.state.caricamentoRiepilogo$.next(false);
    };

    // "nessun dato classificabile" (422) e' gia' segnalato dall'avviso dei punti: per gli altri errori serve un messaggio proprio
    this._rischioAnaliticoService
      .getHeatmap(richiesta)
      .pipe(finalize(fineChiamata))
      .subscribe({
        next: (response) => {
          this.state.heatmap$.next(Array.isArray(response) ? response : []);
        },
        error: (err) => {
          this.state.heatmap$.next([]);
          if (!isNessunDatoClassificabile(err)) this._notificaErrore(err, 'Errore nel caricamento della heatmap del Riepilogo');
        }
      });

    this._rischioAnaliticoService
      .getRiepilogo(richiesta)
      .pipe(finalize(fineChiamata))
      .subscribe({
        next: (response) => this.state.riepilogo$.next(response),
        error: (err) => {
          this.state.riepilogo$.next([]);
          if (!isNessunDatoClassificabile(err)) this._notificaErrore(err, 'Errore nel caricamento del Riepilogo');
        }
      });
  }

  private _popolaLayer(punti: PuntoRischioResponse[]): void {
    this._sourcePunti.clear();
    const features = punti.map((punto) => this._featureDaPunto(punto)).filter((feature): feature is Feature => feature !== null);
    this._sourcePunti.addFeatures(features);
  }

  private _featureDaPunto(punto: PuntoRischioResponse): Feature | null {
    const lat = Number(punto.lat);
    const lon = Number(punto.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    const feature = new Feature({ geometry: new Point(fromLonLat([lon, lat])) });
    feature.setId(punto.puntoUuid);
    feature.set('classeRischio', punto.classeRischio);
    feature.set('punto', punto);
    return feature;
  }

  private _stileFeature(feature: FeatureLike): Style {
    let classe = (feature.get('classeRischio') as string | null) ?? null;
    if (classe === null && this.state.filtri.criterio === 'integrated') {
      classe = 'non disponibile';
    }
    const id = idStileClasse(classe);
    if (!this._styleCache.has(id)) {
      this._styleCache.set(id, this._layerStylesService.buildFeatureStyleFromId(id));
    }
    return this._styleCache.get(id);
  }

  // registra un cerchio colorato per ciascuna classe (6 RiskClass + 7 CriticalityClass + assente)
  private _registraStiliClasse(): void {
    if (!this._layerStylesService.mapStyles) this._layerStylesService.mapStyles = [];

    const registra = (classe: string | null, colore: string) => {
      const id = idStileClasse(classe);
      if (this._layerStylesService.mapStyles.some((s) => s.name === id)) return;
      const { r, g, b } = hexToRgb(colore);
      this._layerStylesService.mapStyles.push({
        name: id,
        image: {
          imageType: 'shape',
          radius: 7,
          fill: { r, g, b, a: 1 },
          stroke: { width: 1.5, r: 255, g: 255, b: 255, a: 1 }
        }
      });
    };

    Object.entries(COLORI_RISK_CLASS).forEach(([classe, colore]) => registra(classe, colore));
    Object.entries(COLORI_CRITICALITY).forEach(([classe, colore]) => registra(classe, colore));
    registra(null, COLORE_CLASSE_ASSENTE);
  }
}
